/**
 * Data Quality API Service
 * Handles all Data Trust & Quality Benchmark Framework API calls
 */

import { api } from './api'
import type {
  AggregateTrustScoreResponse,
  AggregateTrustScoreData,
  AggregateTrustScoreParams,
  SourceMetricsResponse,
  SourceMetricsData,
  SourceMetricsParams,
  ErrorAnalysisResponse,
  ErrorAnalysisData,
  ErrorAnalysisParams,
  SLAStatusResponse,
  SLAStatusData,
  SLAStatusParams,
  HistoricalTrendsResponse,
  HistoricalTrendsData,
  HistoricalTrendsParams,
  SLAConfigurationRequest,
  SLAConfigurationResponse,
  TimeWindow,
  ErrorCategory,
  ComplianceStatus,
  MetricType,
} from '../types/dataQuality'

export class DataQualityService {
  /**
   * Get aggregate trust score with KPI breakdown
   * @param params Query parameters for filtering
   */
  static async getAggregateTrustScore(
    params: AggregateTrustScoreParams = {}
  ): Promise<AggregateTrustScoreData> {
    const queryParams = new URLSearchParams()

    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }
    if (params.quality_threshold !== undefined) {
      queryParams.append('quality_threshold', params.quality_threshold.toString())
    }

    const response = await api.get<AggregateTrustScoreResponse>(
      `/data-quality/trust-score/aggregate?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get detailed quality metrics for a specific data source
   * @param sourceId Data source ID
   * @param params Query parameters for filtering
   */
  static async getSourceMetrics(
    sourceId: number,
    params: SourceMetricsParams = {}
  ): Promise<SourceMetricsData> {
    const queryParams = new URLSearchParams()

    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }

    const response = await api.get<SourceMetricsResponse>(
      `/data-quality/metrics/source/${sourceId}?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get error analysis across all data sources
   * @param params Query parameters for filtering
   */
  static async getErrorAnalysis(
    params: ErrorAnalysisParams = {}
  ): Promise<ErrorAnalysisData> {
    const queryParams = new URLSearchParams()

    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }
    if (params.error_type) {
      queryParams.append('error_type', params.error_type)
    }

    const response = await api.get<ErrorAnalysisResponse>(
      `/data-quality/errors/analysis?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get SLA compliance status and breach information
   * @param params Query parameters for filtering
   */
  static async getSLAStatus(params: SLAStatusParams = {}): Promise<SLAStatusData> {
    const queryParams = new URLSearchParams()

    if (params.data_source_id !== undefined) {
      queryParams.append('data_source_id', params.data_source_id.toString())
    }
    if (params.severity) {
      queryParams.append('severity', params.severity)
    }

    const queryString = queryParams.toString()
    const url = queryString ? `/data-quality/sla/status?${queryString}` : `/data-quality/sla/status`

    const response = await api.get<SLAStatusResponse>(url)

    return response.data.data
  }

  /**
   * Get historical trend analysis for quality metrics
   * @param params Query parameters including metric type and time range
   */
  static async getHistoricalTrends(
    params: HistoricalTrendsParams
  ): Promise<HistoricalTrendsData> {
    const queryParams = new URLSearchParams()

    queryParams.append('metric_type', params.metric_type)
    if (params.days_back !== undefined) {
      queryParams.append('days_back', params.days_back.toString())
    }

    const response = await api.get<HistoricalTrendsResponse>(
      `/data-quality/trends/historical?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Configure custom SLA thresholds for a data source
   * @param config SLA configuration request
   */
  static async configureSLAThresholds(
    config: SLAConfigurationRequest
  ): Promise<SLAConfigurationResponse> {
    const response = await api.post<SLAConfigurationResponse>(
      '/data-quality/sla/configure',
      config
    )

    return response.data
  }

  /**
   * Get dashboard summary with trends
   * @param params Query parameters for filtering
   */
  static async getDashboardSummary(
    params: import('../types/dataQuality').DashboardSummaryParams = {}
  ): Promise<import('../types/dataQuality').DashboardSummaryData> {
    const queryParams = new URLSearchParams()

    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }
    if (params.comparison_days !== undefined) {
      queryParams.append('comparison_days', params.comparison_days.toString())
    }

    const response = await api.get<import('../types/dataQuality').DashboardSummaryResponse>(
      `/data-quality/dashboard/summary?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get pipeline health metrics
   * @param params Query parameters for filtering
   */
  static async getPipelineHealth(
    params: import('../types/dataQuality').PipelineHealthParams = {}
  ): Promise<import('../types/dataQuality').PipelineHealthData> {
    const queryParams = new URLSearchParams()

    if (params.processing_job_id !== undefined) {
      queryParams.append('processing_job_id', params.processing_job_id.toString())
    }
    if (params.data_source_id !== undefined) {
      queryParams.append('data_source_id', params.data_source_id.toString())
    }
    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }

    const response = await api.get<import('../types/dataQuality').PipelineHealthResponse>(
      `/data-quality/pipeline/health?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get schema drift analysis for a specific data source
   * @param sourceId Data source ID
   * @param params Query parameters for filtering
   */
  static async getSchemaDrift(
    sourceId: number,
    params: import('../types/dataQuality').SchemaDriftParams = {}
  ): Promise<import('../types/dataQuality').SchemaDriftData> {
    const queryParams = new URLSearchParams()

    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }

    const response = await api.get<import('../types/dataQuality').SchemaDriftResponse>(
      `/data-quality/schema/drift/${sourceId}?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get schema status summary across all sources
   * @param params Query parameters for filtering
   */
  static async getSchemaStatusSummary(
    params: import('../types/dataQuality').SchemaStatusSummaryParams = {}
  ): Promise<import('../types/dataQuality').SchemaStatusSummaryData> {
    const queryParams = new URLSearchParams()

    if (params.data_source_ids) {
      queryParams.append('data_source_ids', params.data_source_ids)
    }

    const response = await api.get<import('../types/dataQuality').SchemaStatusSummaryResponse>(
      `/data-quality/schema/status-summary?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get source metrics with trend percentages
   * @param sourceId Data source ID
   * @param params Query parameters for filtering
   */
  static async getSourceMetricsWithTrends(
    sourceId: number,
    params: import('../types/dataQuality').SourceMetricsWithTrendsParams = {}
  ): Promise<import('../types/dataQuality').SourceMetricsWithTrendsData> {
    const queryParams = new URLSearchParams()

    if (params.comparison_days !== undefined) {
      queryParams.append('comparison_days', params.comparison_days.toString())
    }
    if (params.include_detailed_trends !== undefined) {
      queryParams.append('include_detailed_trends', params.include_detailed_trends.toString())
    }
    if (params.profile !== undefined) {
      queryParams.append('profile', params.profile)
    }
    if (params.error_time_series_days !== undefined) {
      queryParams.append('error_time_series_days', params.error_time_series_days.toString())
    }

    const url = `/data-quality/metrics/source/${sourceId}/with-trends?${queryParams.toString()}`

    const response = await api.get<import('../types/dataQuality').SourceMetricsWithTrendsResponse>(url)

    return response.data.data
  }

  /**
   * Get time-series trend data for charts
   * @param params Query parameters including metric type
   */
  static async getTimeSeriesTrend(
    params: import('../types/dataQuality').TimeSeriesTrendParams
  ): Promise<import('../types/dataQuality').TimeSeriesTrendData> {
    const queryParams = new URLSearchParams()

    queryParams.append('metric_type', params.metric_type)
    if (params.data_source_id !== undefined) {
      queryParams.append('data_source_id', params.data_source_id.toString())
    }
    if (params.days_back !== undefined) {
      queryParams.append('days_back', params.days_back.toString())
    }
    if (params.granularity) {
      queryParams.append('granularity', params.granularity)
    }
    if (params.include_trend_line !== undefined) {
      queryParams.append('include_trend_line', params.include_trend_line.toString())
    }

    const response = await api.get<import('../types/dataQuality').TimeSeriesTrendResponse>(
      `/data-quality/trends/time-series?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get source reliability comparison table
   * @param params Query parameters for filtering
   */
  static async getSourceReliabilityTable(
    params: import('../types/dataQuality').SourceReliabilityTableParams = {}
  ): Promise<import('../types/dataQuality').SourceReliabilityTableData> {
    const queryParams = new URLSearchParams()

    if (params.time_window) {
      queryParams.append('time_window', params.time_window)
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }

    const response = await api.get<import('../types/dataQuality').SourceReliabilityTableResponse>(
      `/data-quality/source-reliability/table?${queryParams.toString()}`
    )

    return response.data.data
  }

  /**
   * Get active alerts requiring attention
   * @param params Query parameters for filtering
   */
  static async getActiveAlerts(
    params: import('../types/dataQuality').ActiveAlertsParams = {}
  ): Promise<import('../types/dataQuality').ActiveAlertsData> {
    const queryParams = new URLSearchParams()

    if (params.data_source_id !== undefined) {
      queryParams.append('data_source_id', params.data_source_id.toString())
    }
    if (params.severity) {
      queryParams.append('severity', params.severity)
    }
    if (params.alert_type) {
      queryParams.append('alert_type', params.alert_type)
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString())
    }
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString())
    }

    const response = await api.get<import('../types/dataQuality').ActiveAlertsResponse>(
      `/data-quality/alerts/active?${queryParams.toString()}`
    )

    return response.data.data
  }
}

// Export enum types for convenience
export { TimeWindow, ErrorCategory, ComplianceStatus, MetricType }
